import React, { Component } from 'react';
import Iframe from 'react-iframe';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CrossReferenceList from './components/Lists/CrossReferenceList';
import {
    BodyText, SourceFootnotes, FootnoteReference,
} from './components/Core/TextComponents';
import { ThemeContext } from './components/Core/ThemeContext';

const querystring = require('querystring');
import { withAlert } from 'react-alert';

import SEO from "./components/SEO";
import { buildCanonicalUrl, deriveLegacyCanonicalPath } from './utilities/canonicalUrls';
import { buildEntitySeoDescription, buildEntitySeoTitle } from './utilities/seo';
import osisConverters from './utilities/osisConversions';
import placePageOverrides from './data/placePageOverrides.json';

const getPlacePageOverride = (placeRef = '') => (placeRef ? placePageOverrides[placeRef] || null : null);

const normalizePlaceText = (value = '') => `${value}`
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\(\s*([^)]*?)\s*\)\s*or\s+/i, '')
    .replace(/^\(?[A-Za-z0-9]+\.\s*\d+:\d+\)?\s*(?:or\s+)?/i, '')
    .replace(/^\(?\d+:\d+\)?\s*(?:or\s+)?/i, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

const extractLeadSentence = (value = '') => {
    const normalized = normalizePlaceText(value);
    if (!normalized) return '';

    const match = normalized.match(/^.*?[.!?](?:\s|$)/);
    return match ? match[0].trim() : normalized;
};

const trimTrailingPunctuation = (value = '') => `${value}`.replace(/[.,;:!?]+$/g, '').trim();

const toSentence = (value = '') => {
    const normalized = normalizePlaceText(value);
    if (!normalized) return '';
    return /[.!?]$/.test(normalized) ? normalized : `${normalized}.`;
};

const stripReferenceCitations = (value = '') => `${value}`
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\((?:[^)]*\d+:\d+[^)]*)\)/g, '')
    .replace(/\[\s*[^\]]*\d+:\d+[^\]]*\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const buildSummarySourceText = (value = '') => normalizePlaceText(stripReferenceCitations(value));

const buildPlaceType = (text = '') => {
    const normalized = normalizePlaceText(text).toLowerCase();
    if (!normalized) return '';

    const candidates = [
        ['valley', 'valley'],
        ['mount', 'mountain'],
        ['hill', 'hill'],
        ['river', 'river'],
        ['wilderness', 'wilderness place'],
        ['city', 'city'],
        ['town', 'town'],
        ['village', 'village'],
        ['country', 'region'],
        ['region', 'region'],
        ['plain', 'plain'],
        ['camp', 'camp'],
        ['place', 'place'],
    ];

    const match = candidates.find(([needle]) => normalized.includes(`${needle} `) || normalized.includes(` ${needle}`) || normalized.startsWith(needle));
    return match ? match[1] : '';
};

const buildPlaceFeature = (place = {}) => {
    const feature = normalizePlaceText(place.feature || '');
    if (feature) return feature.toLowerCase();

    const inferredType = buildPlaceType(place.dictText || '');
    return inferredType || 'place';
};

const buildPlaceMeaningLabel = (place = {}) => {
    const meaning = trimTrailingPunctuation(place.meaning || '');
    if (!meaning) return '';
    return `Meaning: ${meaning}`;
};

const buildPlaceFallbackLead = (place = {}) => {
    const name = place.name || 'This place';
    const meaning = trimTrailingPunctuation(place.meaning || '');
    const feature = buildPlaceFeature(place);

    if (meaning) {
        return `${name} means "${meaning}" and is a biblical ${feature}.`;
    }

    return `${name} is a biblical ${feature}.`;
};

const buildPlaceOverview = (place = {}, mapAvailable = false) => {
    const pageOverride = getPlacePageOverride(place.ref);
    if (pageOverride?.overview) {
        return pageOverride.overview;
    }

    const summaryLead = extractLeadSentence(buildSummarySourceText(place.dictText || ''));
    const referenceCount = Array.isArray(place.verses) ? place.verses.length : 0;
    const parts = [];

    if (summaryLead) {
        parts.push(toSentence(summaryLead));
    } else {
        parts.push(buildPlaceFallbackLead(place));
    }

    if (referenceCount > 0) {
        parts.push(`It is mentioned in ${referenceCount} Bible passage${referenceCount === 1 ? '' : 's'} on ai-BIBLE.`);
    }

    if (mapAvailable) {
        parts.push(`This page also includes a modern-day map and linked Bible references.`);
    }

    return parts.join(' ');
};

const splitSourceSentences = (value = '') => `${value}`
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

const lowerFirstCharacter = (value = '') => value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : '';

const buildPlaceMeaning = (place = {}) => {
    const meaning = trimTrailingPunctuation(place.meaning || '');
    if (!meaning) return '';
    return `${place.name || 'This place'} means "${meaning}."`;
};

const buildPlaceLocationContext = (place = {}) => {
    const name = place.name || 'This place';
    const leadSentence = extractLeadSentence(buildSummarySourceText(place.dictText || ''));
    const normalizedLead = normalizePlaceText(leadSentence);

    if (!normalizedLead) {
        return buildPlaceFallbackLead(place);
    }

    const locationMatch = normalizedLead.match(/(a|an|the)\s+(city|place|valley|hill|mountain|country|region|town|village|wilderness(?: place)?|river|camp)\b.*$/i);
    if (locationMatch) {
        return `${name} is ${trimTrailingPunctuation(locationMatch[0])}.`;
    }

    const trimmedLead = trimTrailingPunctuation(normalizedLead)
        .replace(new RegExp(`^${name}\\s+`, 'i'), '')
        .trim();

    if (trimmedLead) {
        return `${name} is ${lowerFirstCharacter(trimmedLead)}.`;
    }

    return buildPlaceFallbackLead(place);
};

const buildPlaceNarrativeContext = (place = {}) => {
    const sentences = splitSourceSentences(buildSummarySourceText(place.dictText || ''));
    if (sentences.length <= 1) return '';

    return sentences
        .slice(1, 3)
        .map((sentence) => toSentence(sentence))
        .filter(Boolean)
        .join(' ');
};

const buildPlaceSignificance = (place = {}) => {
    const pageOverride = getPlacePageOverride(place.ref);
    if (pageOverride?.biblicalSignificance) {
        return pageOverride.biblicalSignificance;
    }

    const locationContext = buildPlaceLocationContext(place);
    const narrativeContext = buildPlaceNarrativeContext(place);
    const referenceCount = Array.isArray(place.verses) ? place.verses.length : 0;

    if (narrativeContext) {
        return `AI summary: ${locationContext} ${narrativeContext}`;
    }

    if (referenceCount > 0) {
        return `AI summary: ${locationContext} It appears in ${referenceCount} Bible passage${referenceCount === 1 ? '' : 's'}, and this page gathers those linked references to show its place in the biblical story.`;
    }

    return `AI summary: ${locationContext}`;
};

const getMapEmbedUrl = (latitude, longitude) => {
    const lat = Number.parseFloat(latitude);
    const lon = Number.parseFloat(longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';

    const latPadding = 0.12;
    const lonPadding = 0.18;
    const bbox = [
        lon - lonPadding,
        lat - latPadding,
        lon + lonPadding,
        lat + latPadding,
    ].join('%2C');

    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lon}`;
};

const formatCoordinate = (value) => {
    const numericValue = Number.parseFloat(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const getMapLinkUrl = (latitude, longitude) => {
    const lat = Number.parseFloat(latitude);
    const lon = Number.parseFloat(longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';

    return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=12/${lat}/${lon}`;
};

const normalizeReferenceLabelToOsis = (value = '') => {
    const compact = `${value}`
        .replace(/[()[\]]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    if (!compact) return '';

    const normalized = compact
        .replace(/^([1-3])\s+([A-Za-z])/i, '$1$2')
        .replace(/\s*\.\s*/g, '.')
        .replace(/\s*:\s*/g, '.')
        .replace(/\s+/g, '');

    return normalized;
};

const resolveSummaryReferenceOsis = (label = '', href = '') => {
    const hashRef = `${href}`.split('#')[1] || '';
    const normalizedHashRef = normalizeReferenceLabelToOsis(hashRef);

    if (normalizedHashRef) return normalizedHashRef;

    return normalizeReferenceLabelToOsis(label);
};

const buildAiBibleReferenceHref = (osisRef = '') => {
    const normalizedOsisRef = normalizeReferenceLabelToOsis(osisRef);
    if (!normalizedOsisRef) return '';
    return osisConverters.toWebReadPath(normalizedOsisRef);
};

const formatOsisReferenceLabel = (osisRef = '') => `${osisRef}`
    .replace(/^([1-3])([A-Za-z])/, '$1 $2')
    .replace(/\.(\d+)\.(\d+)$/, ' $1:$2')
    .replace(/\.(\d+)$/, ' $1');

const renderDictionarySummary = (value = '') => {
    const text = `${value}`;
    const parts = [];
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;

    while ((match = linkPattern.exec(text)) !== null) {
        const [raw, label, href] = match;
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            parts.push(text.slice(lastIndex, matchIndex));
        }

        const osisRef = resolveSummaryReferenceOsis(label, href);
        const aiBibleHref = buildAiBibleReferenceHref(osisRef);

        if (aiBibleHref) {
            parts.push(
                <a key={`${osisRef}-${matchIndex}`} href={aiBibleHref}>
                    {label}
                </a>,
            );
        } else {
            parts.push(raw);
        }

        lastIndex = matchIndex + raw.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
};

const sectionCardStyles = {
    pageShell: {
        width: 'min(1120px, calc(100% - 36px))',
        margin: '28px auto 70px',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    previewGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)',
        gap: 16,
        alignItems: 'stretch',
    },
    shell: {
        display: 'flex',
        background: '#fff',
        borderRadius: 15,
        overflow: 'hidden',
        boxShadow: '0 10px 24px rgba(86, 96, 110, 0.07)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    strip: {
        width: 14,
        minWidth: 14,
        background: 'var(--color-grad-1)',
    },
    content: {
        flex: 1,
        background: 'linear-gradient(180deg, #fffefb 0%, #f7fbfb 100%)',
    },
    summaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '18px 22px',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    summaryText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    summaryEyebrow: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    summaryTitle: {
        margin: 0,
        color: 'rgba(0, 0, 0, 0.8)',
        fontSize: '1.05em',
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
    summaryDivider: {
        height: 1,
        background: 'rgba(188, 198, 208, 0.32)',
        margin: '0 20px',
    },
    summaryBody: {
        padding: '18px 22px 22px',
        color: 'rgba(0, 0, 0, 0.76)',
    },
    collapsedBody: {
        maxHeight: 0,
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    },
    heroShell: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 18,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 18px 42px rgba(86, 96, 110, 0.1)',
        marginBottom: 18,
    },
    heroStrip: {
        width: 22,
        minWidth: 22,
        background: 'var(--color-grad-1)',
    },
    heroBody: {
        padding: '30px 34px 28px',
        flex: 1,
    },
    heroEyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    heroTitle: {
        margin: '6px 0 0 0',
        fontSize: 'clamp(2.2em, 5vw, 3.1em)',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
    },
    heroMeta: {
        margin: '10px 0 0 0',
        color: 'rgba(0, 0, 0, 0.58)',
        fontSize: '1.02em',
    },
    heroSummary: {
        marginTop: 14,
        marginBottom: 0,
        fontSize: '1.08em',
        lineHeight: 1.65,
    },
    heroFacts: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 10,
        marginTop: 20,
    },
    heroFact: {
        padding: '10px 12px',
        border: '1px solid rgba(188, 198, 208, 0.42)',
        borderRadius: 10,
        background: 'linear-gradient(145deg, #fff 0%, #f4faf9 100%)',
    },
    heroFactLabel: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.68em',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    heroFactValue: {
        margin: '3px 0 0',
        color: 'rgba(23, 38, 83, 0.9)',
        fontSize: '0.88em',
    },
};

function SectionCard({
    title,
    children,
    startsOpen = true,
    eyebrow = 'Section',
    open: controlledOpen,
    onToggle,
}) {
    const [internalOpen, setInternalOpen] = React.useState(startsOpen);
    const isControlled = typeof controlledOpen === 'boolean';
    const open = isControlled ? controlledOpen : internalOpen;

    const setOpen = (nextValue) => {
        if (!isControlled) {
            setInternalOpen(nextValue);
        }

        if (onToggle) {
            onToggle(nextValue);
        }
    };

    return (
        <section style={sectionCardStyles.shell}>
            <div style={sectionCardStyles.strip} aria-hidden="true" />
            <div style={sectionCardStyles.content}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={sectionCardStyles.summaryButton}
                >
                    <span style={sectionCardStyles.summaryText}>
                        <span style={sectionCardStyles.summaryEyebrow}>{eyebrow}</span>
                        <h2 style={sectionCardStyles.summaryTitle}>{title}</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={sectionCardStyles.summaryDivider} aria-hidden="true" />
                <div
                    aria-hidden={!open}
                    style={{
                        ...sectionCardStyles.summaryBody,
                        ...(open ? null : sectionCardStyles.collapsedBody),
                    }}
                >
                    {children}
                </div>
            </div>
        </section>
    );
}

function HeroCard({ title, meta, summary, facts = [] }) {
    return (
        <div style={sectionCardStyles.heroShell}>
            <div style={sectionCardStyles.heroStrip} />
            <div style={sectionCardStyles.heroBody}>
                <p style={sectionCardStyles.heroEyebrow}>Bible Location</p>
                <h1 style={sectionCardStyles.heroTitle}>{title}</h1>
                {meta ? (
                    <p style={sectionCardStyles.heroMeta}>{meta}</p>
                ) : null}
                {summary ? (
                    <BodyText style={sectionCardStyles.heroSummary}>
                        {summary}
                    </BodyText>
                ) : null}
                {facts.length > 0 ? (
                    <div style={sectionCardStyles.heroFacts}>
                        {facts.map((fact) => (
                            <div key={fact.label} style={sectionCardStyles.heroFact}>
                                <p style={sectionCardStyles.heroFactLabel}>{fact.label}</p>
                                <p style={sectionCardStyles.heroFactValue}>{fact.value}</p>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function PlaceDetailGrid({ place = {}, mapAvailable = false }) {
    const details = [
        ['Place meaning', place.meaning],
        ['Latitude', mapAvailable ? formatCoordinate(place.latitude) : ''],
        ['Longitude', mapAvailable ? formatCoordinate(place.longitude) : ''],
        ['Bible references', Array.isArray(place.verses) ? place.verses.length : ''],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {details.map(([label, value]) => (
                <div key={label} style={{ padding: '10px 12px', borderRadius: 9, background: '#fffaf0', border: '1px solid rgba(220, 163, 63, 0.3)' }}>
                    <p style={{ margin: 0, fontSize: '0.68em', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(120, 93, 47, 0.82)' }}>{label}</p>
                    <p style={{ margin: '3px 0 0', color: 'rgba(23, 38, 83, 0.9)' }}>{value}</p>
                </div>
            ))}
        </div>
    );
}

const buildAiGeneratedSource = () => ({
    footnote: 'AI-assisted summary prepared for ai-BIBLE and stored as page content, based on dictionary material, place metadata, and linked Bible cross references.',
});

const getSortedFootnoteEntries = (sources = []) => [...sources].sort((a, b) => {
    if (!a?.footnote) return -1;
    if (!b?.footnote) return 1;
    if (a.footnote > b.footnote) return 1;
    if (b.footnote > a.footnote) return -1;
    return 0;
});

const getFootnoteNumber = (sortedSources = [], predicate) => {
    let counter = 0;

    for (let i = 0; i < sortedSources.length; i += 1) {
        const source = sortedSources[i];
        if (!source?.footnote) continue;
        counter += 1;
        if (predicate(source)) return counter;
    }

    return null;
};

class PlaceResult extends Component {
    static contextType = ThemeContext;

    constructor() {
        super();

        this.state = {
            data: {
                where: [],
            },
            sources: [],
            ref: '',
            sourcesOpen: false,
        };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    getPlace()
    {
        if (window.location.pathname.includes('/place'))
            return { ...querystring.parse(window.location.search.substr(1)), pathPrefix: '/data/' }
        else
            return {
                rec: window.location.pathname
                    .replace('/location/', '')
                    .replace(/\/+$/g, ''),
                pathPrefix: '/data/'
            }
    }

    componentDidMount() {
        console.log(`PagePlacesResult: ComponentDidMount: ${new Date().getSeconds()}`);
        const values = this.getPlace();
        const { token } = localStorage;
        
        this.context.loading(true);

        if (window.web_version && values.rec) {
            const normalizedPath = deriveLegacyCanonicalPath(window.location.pathname, window.location.search);
            if (`${window.location.pathname}${window.location.search}` !== normalizedPath) {
                window.history.replaceState({}, '', normalizedPath);
            }
        }

        this.setState({ ref: values.rec });
        window.addEventListener('hashchange', this.handleHashChange);
        document.addEventListener('click', this.handleDocumentClick, true);
        this.openSourcesForFootnoteHash(window.location.hash);

        let endpoint = `/v1/places/known?rec=${values.rec}`;

        if (process.env.REACT_APP_AWS_API)
        {
            endpoint = window.API_BASE + endpoint;
        }

        // use local json version
        endpoint = `${values.pathPrefix}places/${values.rec}.json`

        if (endpoint !== '') {
            fetch(endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((res) => res.json())
                .then((response) => {
                    const data = { where: response.where.place };
                    const { sources } = response;

                    this.setState({
                        data, sources,
                    });

                    this.context.loading(false);
                }).catch((e)=>{
                    console.log('exception: ', e);
                    this.props.alert.show('could not connect to server');
                });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('hashchange', this.handleHashChange);
        document.removeEventListener('click', this.handleDocumentClick, true);
    }

    componentDidUpdate() {
        console.log(`PagePlacesResult: ComponentDidUpdate: ${new Date().getSeconds()}`);

        const values = this.getPlace();

        console.log('compare', this.state.ref, values.rec);
        if (this.state.ref !== values.rec) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ ref: values.rec });
            this.componentDidMount();
        }
    }

    handleHashChange = () => {
        this.openSourcesForFootnoteHash(window.location.hash);
    }

    handleDocumentClick = (event) => {
        const footnoteLink = event.target.closest('a[href^="#footnote"]');
        if (!footnoteLink) return;
        this.openSourcesForFootnoteHash(footnoteLink.getAttribute('href'));
    }

    openSourcesForFootnoteHash = (hash = '') => {
        if (!/^#footnote\d+$/.test(hash)) return;

        this.setState({ sourcesOpen: true }, () => {
            window.requestAnimationFrame(() => {
                const target = document.querySelector(hash);
                if (target) {
                    target.scrollIntoView({ block: 'start' });
                }
            });
        });
    }

    render() {
        const { name } = this.state.data.where;
        const searchedRec = this.getPlace().rec;
        const canonical = buildCanonicalUrl(`/location/${searchedRec}`);
        const place = this.state.data.where || {};
        const mapEmbedUrl = getMapEmbedUrl(place.latitude, place.longitude);
        const mapLinkUrl = getMapLinkUrl(place.latitude, place.longitude);
        const mapAvailable = Boolean(mapEmbedUrl);
        const placeOverview = buildPlaceOverview(place, mapAvailable);
        const placeSignificance = buildPlaceSignificance(place);
        const placeMeaningLabel = buildPlaceMeaningLabel(place);
        const aiGeneratedSource = buildAiGeneratedSource();
        const allSources = [...this.state.sources, aiGeneratedSource];
        const sortedFootnotes = getSortedFootnoteEntries(allSources);
        const aiGeneratedFootnoteNumber = getFootnoteNumber(sortedFootnotes, (source) => source === aiGeneratedSource);
        const dictionarySummaryFootnoteNumber = getFootnoteNumber(
            sortedFootnotes,
            (source) => source?.footnote === this.state.sources?.[2]?.footnote,
        );
        const crossReferenceCount = (place.verses || []).length;
        const heroMeta = [
            placeMeaningLabel,
            crossReferenceCount > 0 ? `${crossReferenceCount} cross reference${crossReferenceCount === 1 ? '' : 's'}` : '',
            mapAvailable ? 'Modern-day map available' : '',
        ].filter(Boolean).join(' · ');
        const seoTitle = name ? buildEntitySeoTitle({
            type: 'place',
            name,
            summary: placeOverview || place.dictText || '',
            crossReferenceCount,
            mapAvailable,
        }) : 'Bible Places | ai-BIBLE';

        document.title = seoTitle;

        // const noteCounter = (sources) => {
        //     let count = 0;

        //     if (sources == null) return notesCounted;
        //     if (sources === []) return notesCounted;

        //     for (let i = 0; i < sources.length; i += 1) {
        //         const source = sources[i];
        //         if (source) {
        //             if (source === 1) {
        //                 count += 1;
        //             } else {
        //                 for (let j = 0; j < source.length; j += 1) {
        //                     const note = source[j];
        //                     if (note) {
        //                         if (Object.prototype.hasOwnProperty.call(note, 'footnote')) count += 1;
        //                     }
        //                 }
        //             }
        //         }
        //     }

        //     notesCounted += count;
        //     return notesCounted;
        // };

        const description = buildEntitySeoDescription({
            type: 'place',
            name: name || 'This place',
            summary: placeOverview || this.state?.data?.where?.dictText || '',
            crossReferenceCount,
            mapAvailable,
        });
        const firstCrossReference = Array.isArray(place.verses) && place.verses.length > 0
            ? place.verses[0]
            : '';
        const firstCrossReferenceHref = buildAiBibleReferenceHref(firstCrossReference);
        const heroFacts = [
            place.meaning ? { label: 'Place meaning', value: place.meaning } : null,
            mapAvailable ? { label: 'Coordinates', value: `${formatCoordinate(place.latitude)}, ${formatCoordinate(place.longitude)}` } : null,
            firstCrossReference ? { label: 'First appears', value: formatOsisReferenceLabel(firstCrossReference) } : null,
        ].filter(Boolean);
        const structuredData = name ? {
            '@context': 'https://schema.org',
            '@type': 'Place',
            name,
            description: placeOverview || undefined,
            url: canonical,
            geo: mapAvailable ? {
                '@type': 'GeoCoordinates',
                latitude: Number(place.latitude),
                longitude: Number(place.longitude),
            } : undefined,
            sameAs: (place.verses || []).slice(0, 10).map(buildAiBibleReferenceHref).filter(Boolean),
            mainEntityOfPage: canonical,
        } : null;

        const why = {
            cross_references: (this.state.data.where.verses || []),
        };

        return (
            <div className="entity-page-viewport">
                <SEO 
                    canonical = {canonical}
                    title = { seoTitle }
                    description = { description }
                    type = 'article'
                    structuredData = { structuredData }
                />
                <div style={sectionCardStyles.pageShell}>
                    <HeroCard
                        title={place.name}
                        meta={heroMeta}
                        summary={placeOverview ? (
                            <>
                                {placeOverview}
                                {firstCrossReferenceHref ? (
                                    <>
                                        {' '}
                                        <a href={firstCrossReferenceHref}>
                                            First appears in {formatOsisReferenceLabel(firstCrossReference)}
                                        </a>.
                                    </>
                                ) : null}
                                <FootnoteReference number={aiGeneratedFootnoteNumber} />
                            </>
                        ) : null}
                        facts={heroFacts}
                    />
                    <div style={sectionCardStyles.stack}>
                        <div className="entity-preview-grid" style={sectionCardStyles.previewGrid}>
                            {placeSignificance ? (
                                <SectionCard title="Why this place matters" eyebrow="Interpretation">
                                    <BodyText>
                                        {placeSignificance}
                                        <FootnoteReference number={aiGeneratedFootnoteNumber} />
                                    </BodyText>
                                </SectionCard>
                            ) : null}
                            <SectionCard title="Location Details" eyebrow="At a glance">
                                <PlaceDetailGrid place={place} mapAvailable={mapAvailable} />
                            </SectionCard>
                        </div>
                        {mapEmbedUrl ? (
                            <SectionCard title="Map and Location (Modern Day)" eyebrow="Geography">
                                <Iframe
                                    width="100%"
                                    height="350"
                                    frameborder="0"
                                    scrolling="no"
                                    marginheight="0"
                                    marginwidth="0"
                                    src={mapEmbedUrl}
                                    loading="lazy"
                                    title={`Map showing ${place.name}`}
                                    referrerpolicy="no-referrer-when-downgrade"
                                />
                                <br />
                                {mapLinkUrl ? (
                                    <small><a href={mapLinkUrl}>View Larger Map</a></small>
                                ) : null}
                            </SectionCard>
                        ) : null}
                        {(this.state.data.where.dictText)
                            ? (
                                <SectionCard title="Easton's Dictionary · Historical and Archaic" startsOpen={false} eyebrow="Reference">
                                    <BodyText>
                                        {renderDictionarySummary(this.state.data.where.dictText)}
                                        {' '}
                                        {dictionarySummaryFootnoteNumber ? (
                                            <FootnoteReference number={dictionarySummaryFootnoteNumber} />
                                            ) : null}
                                    </BodyText>
                                    <BodyText style={{ marginTop: 12, fontSize: '0.88em', color: 'rgba(92, 80, 52, 0.9)' }}>
                                        Source note: this 1893 dictionary language reflects its period and may be culturally insensitive or outdated. It is presented as historical source material, not as ai-BIBLE’s language, interpretation, or endorsement.
                                    </BodyText>
                                </SectionCard>
                            )
                            : null}
                        <SectionCard title="Cross References" startsOpen={false} eyebrow="Bible Text">
                            <CrossReferenceList
                                why={why}
                                sources={(this.state.sources.length > 0) ? [this.state.sources[1]] : null}
                                figureNumber={1}
                            />
                        </SectionCard>
                        <SectionCard
                            title="Footnotes, Sources and Credits"
                            startsOpen={false}
                            eyebrow="Sources"
                            open={this.state.sourcesOpen}
                            onToggle={(sourcesOpen) => this.setState({ sourcesOpen })}
                        >
                            <SourceFootnotes footnotes={allSources} footnoteStart={1} />
                        </SectionCard>
                    </div>
                </div>
            </div>
        );
    }
}

export default withAlert()(PlaceResult);
